/**
 * Smart Search API Client
 *
 * Wraps the smart search navigation endpoint.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type { SearchResponseData } from '../types';

const SEARCH_PREFIX = API_ROUTES.SETTINGS;

export type SmartSearchLangCode = 'en' | 'enUS' | 'enGB' | 'de' | 'fr' | 'tr';

/** Map tenant/app locale values to the backend-supported smart-search lang_code enum. */
export function resolveSmartSearchLangCode(locale: string | null | undefined): SmartSearchLangCode {
  const normalized = (locale ?? '').trim().replace(/_/g, '-').toLowerCase();

  switch (normalized) {
    case 'en-gb':
    case 'engb':
      return 'enGB';
    case 'de':
    case 'de-de':
      return 'de';
    case 'fr':
    case 'fr-fr':
      return 'fr';
    case 'tr':
    case 'tr-tr':
      return 'tr';
    case 'enus':
    case 'en-us':
    case 'en':
    default:
      return 'en';
  }
}

/** GET /settings/api/v1/search?q={query} — navigation search.
 *  createApiClient unwraps ApiResponse<T> in its interceptor, so response.data
 *  is already the inner payload (SearchResponseData = SearchResultItem[]).
 */
export function fetchSearchResults(query: string, langCode: SmartSearchLangCode) {
  const api = getApiClient();
  return api.get<SearchResponseData>(`${SEARCH_PREFIX}/search`, {
    params: { q: query, lang_code: langCode },
  });
}
